import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { api, ApiError, useApi } from "../api.ts";
import { useAuth } from "../auth.tsx";
import { Badge, Button, cn, ErrorText, Icon, LoadError, Loading, spot, useToast, type IconName } from "../ui/index.tsx";
import { LogoMark } from "../ui/logo.tsx";
import { GameScene } from "../three/GameScene.tsx";
import { themeFor, variantFor } from "../three/themes.ts";
import { CompatBadges, DetectionBadge, GameArt, GameStatusBadge, NotForSale, startCheckout, useStripeEnabled } from "./_shared.tsx";
import type { Game, PublicStatus, StatusDay, StatusLevel } from "../../shared/types.ts";
import { useDocumentTitle } from "../title.tsx";

/** Game accent at a given strength; `--game` is set on the page root. */
const mix = (pct: number) => `color-mix(in srgb, var(--game) ${pct}%, transparent)`;

/** Fades children up the first time they scroll into view. */
function Reveal({ children, delay = 0, className }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) { setSeen(true); io.disconnect(); } }, { rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={cn(seen ? "animate-rise" : "opacity-0", className)} style={{ animationDelay: `${delay}ms` }}>{children}</div>;
}

function SectionHead({ kicker, title, aside }: { kicker: string; title: ReactNode; aside?: ReactNode }) {
  return (
    <Reveal className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="kicker mb-3" style={{ color: "var(--game)" }}>{kicker}</div>
        <h2 className="text-3xl font-semibold uppercase leading-none tracking-tight sm:text-4xl">{title}</h2>
      </div>
      {aside}
    </Reveal>
  );
}

const LEVELS: Record<StatusLevel, { bar: string; label: string }> = {
  operational: { bar: "bg-ok", label: "Operational" },
  maintenance: { bar: "bg-info", label: "Maintenance" },
  degraded: { bar: "bg-warn", label: "Degraded" },
  partial: { bar: "bg-[#ff8a3d]", label: "Partial outage" },
  major: { bar: "bg-bad", label: "Major outage" },
};

/** "Lifetime" or the longest duration is highlighted as best value. */
const bestIndex = (g: Game) => {
  const life = g.pricing_tiers.findIndex((t) => /lifetime/i.test(t.dur));
  return life >= 0 ? life : g.pricing_tiers.length - 1;
};

const scrollToPricing = () => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });

// ── Hero ──

/** Key art that floats and tilts towards the pointer. */
function KeyArt({ game }: { game: Game }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const calm = useReducedMotion();
  return (
    <div className="[perspective:1400px]"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setTilt({ x: ((e.clientY - r.top) / r.height - 0.5) * -10, y: ((e.clientX - r.left) / r.width - 0.5) * 12 });
      }}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}>
      <motion.div animate={calm ? undefined : { y: [0, -8, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
        <div className="pane hud hud-accent relative overflow-hidden bg-surface/80 backdrop-blur-xl transition-transform duration-300 ease-out"
          style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`, ["--c" as string]: "var(--game)", boxShadow: `0 30px 80px -30px ${mix(45)}` } as CSSProperties}>
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="kicker text-[10px]">{game.id} // key art</span>
            <span className="size-2 rounded-full animate-pulse-soft" style={{ background: "var(--game)", boxShadow: `0 0 10px var(--game)` }} />
          </div>
          <div className="relative">
            <GameArt game={game} className="aspect-[460/215]" />
            <div className="absolute inset-0 bg-gradient-to-t from-surface/90 via-transparent to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1/3 animate-scan bg-gradient-to-b from-transparent via-white/[0.05] to-transparent" />
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="truncate font-display text-sm font-semibold uppercase tracking-wide">{game.name}</span>
            <span className="kicker shrink-0 text-[10px]">{game.category}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Hero({ game }: { game: Game }) {
  const { me } = useAuth();
  const theme = themeFor(game.id, game.category);
  const { scrollY } = useScroll();
  const sceneY = useTransform(scrollY, [0, 700], [0, 140]);
  const sceneOpacity = useTransform(scrollY, [0, 700], [1, 0.25]);
  const hasPricing = game.pricing_tiers.length > 0;
  const comingSoon = !game.purchasable && game.status !== "discontinued";
  return (
    <section className="relative isolate min-h-[85vh] overflow-hidden">
      <motion.div style={{ y: sceneY, opacity: sceneOpacity }} className="absolute inset-x-0 -top-36 bottom-0 [mask-image:linear-gradient(to_bottom,#000_72%,transparent)]">
        <GameScene variant={variantFor(game.id, game.category)} accent={theme.a} accent2={theme.b} />
      </motion.div>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(60rem 32rem at 72% 42%, ${mix(13)}, transparent 70%)` }} />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-bg/85 via-bg/35 to-transparent lg:from-bg/75 lg:via-transparent" />
      <div className="grid-floor pointer-events-none absolute inset-x-0 bottom-0 h-1/2 opacity-40 [transform:scaleY(-1)]" />

      <div className="relative mx-auto grid min-h-[85vh] max-w-6xl items-center gap-12 px-6 pb-20 pt-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <div className="min-w-0">
          <div className="kicker mb-5 flex items-center gap-2 animate-rise" style={{ color: "var(--game)" }}>
            <span className="size-1.5 rounded-full" style={{ background: "var(--game)", boxShadow: "0 0 10px var(--game)" }} />
            // product / {game.id}
          </div>
          <h1 className="break-words text-[2.7rem] font-bold uppercase leading-[0.95] tracking-tight animate-rise [animation-delay:80ms] sm:text-6xl lg:text-7xl">{game.name}</h1>
          <div className="mt-4 h-1 w-24 rounded-full animate-rise [animation-delay:120ms]" style={{ background: `linear-gradient(90deg, var(--game), var(--game2))` }} />
          <div className="mt-5 flex flex-wrap items-center gap-2 animate-rise [animation-delay:160ms]">
            <GameStatusBadge status={game.status} />
            <DetectionBadge status={game.detection_status} />
            <Badge>{game.category}</Badge>
          </div>
          <CompatBadges game={game} className="mt-2 animate-rise [animation-delay:180ms]" />
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted animate-rise [animation-delay:200ms]">{game.description}</p>
          <div className="mt-9 flex flex-wrap items-center gap-3 animate-rise [animation-delay:260ms]">
            {game.purchasable && hasPricing ? (
              <Button variant="primary" size="lg" icon="zap" className="glow-border h-12 px-6" onClick={scrollToPricing}>{me ? "Buy now" : "Get started"}</Button>
            ) : comingSoon ? (
              <Button variant="primary" size="lg" icon="bell" className="glow-border h-12 px-6" onClick={scrollToPricing}>Notify me</Button>
            ) : (
              <Link to="/games"><Button variant="primary" size="lg" className="h-12 px-6">All games</Button></Link>
            )}
            <Link to="/status"><Button size="lg" icon="activity" className="h-12 px-6">Live status</Button></Link>
          </div>
          {game.purchasable && hasPricing && <p className="mt-5 font-mono text-xs uppercase tracking-[0.14em] text-faint animate-rise [animation-delay:300ms]">from <span className="text-fg">{game.pricing_tiers[0]!.price}</span> // {game.pricing_tiers[0]!.dur}</p>}
        </div>
        <div className="min-w-0 animate-rise [animation-delay:240ms] lg:justify-self-end lg:w-full lg:max-w-md"><KeyArt game={game} /></div>
      </div>
    </section>
  );
}

// ── Features ──

const FEATURE_ICONS: IconName[] = ["eye", "zap", "shield", "gamepad", "activity", "monitor"];

function Arsenal({ features }: { features: string[] }) {
  if (features.length === 0) return null;
  const big = features.slice(0, 6), rest = features.slice(6);
  return (
    <section className="mx-auto max-w-6xl px-6 py-20">
      <SectionHead kicker="// feature arsenal" title={<>Loadout <span className="text-muted">({features.length})</span></>} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {big.map((f, i) => (
          <Reveal key={f} delay={i * 70}>
            <div onMouseMove={spot} className="pane hud spotlight group flex h-full flex-col gap-5 p-5 transition-colors hover:border-line-strong">
              <div className="flex items-start justify-between">
                <div className="grid size-11 place-items-center rounded-xl ring-1 transition-transform group-hover:scale-110" style={{ background: mix(14), color: "var(--game)", ["--tw-ring-color" as string]: mix(30) } as CSSProperties}>
                  <Icon name={FEATURE_ICONS[i % FEATURE_ICONS.length]!} className="size-5" />
                </div>
                <span className="font-mono text-xs tabular-nums text-faint">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="text-xl font-semibold uppercase tracking-tight">{f}</h3>
              <div className="mt-auto h-px w-full" style={{ background: `linear-gradient(90deg, ${mix(55)}, transparent)` }} />
            </div>
          </Reveal>
        ))}
      </div>
      {rest.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {rest.map((f, i) => (
            <li key={f} className="animate-rise" style={{ animationDelay: `${i * 40}ms` }}>
              <span className="inline-flex items-center gap-2 rounded-lg border border-line bg-surface/70 px-3 py-1.5 text-sm text-muted backdrop-blur">
                <Icon name="check" className="size-3.5" style={{ color: "var(--game)" }} />{f}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Telemetry ──

function UptimeStrip({ days }: { days: StatusDay[] }) {
  const recent = days.slice(-30);
  return (
    <div className="flex h-10 items-stretch gap-[3px]" role="img" aria-label="Uptime over the last 30 days">
      {recent.map((d) => {
        const lv = d.level ? LEVELS[d.level] : null;
        return <div key={d.date} title={`${d.date}: ${lv?.label ?? "No data"}`} className={cn("min-w-0 flex-1 rounded-[3px] transition-transform hover:scale-y-110", lv?.bar ?? "bg-line")} />;
      })}
    </div>
  );
}

function Telemetry({ game, status }: { game: Game; status: PublicStatus | undefined }) {
  const row = status?.games.find((g) => g.id === game.id);
  const uptime = row?.uptime ?? null;
  const cells: { label: string; value: ReactNode }[] = [
    { label: `${status?.window_days ?? 90}d uptime`, value: <span className="font-display text-3xl font-semibold tabular-nums">{uptime === null ? "—" : `${uptime.toFixed(uptime === 100 ? 0 : 2)}%`}</span> },
    { label: "status", value: <GameStatusBadge status={game.status} /> },
    { label: "detection", value: <DetectionBadge status={game.detection_status} /> },
    { label: "category", value: <span className="font-display text-lg font-semibold uppercase">{game.category}</span> },
    { label: "compatibility", value: <CompatBadges game={game} /> },
    { label: "updates", value: game.status === "discontinued"
      ? <span className="flex items-center gap-1.5 text-sm font-medium text-muted"><Icon name="info" className="size-4" />No further updates</span>
      : <span className="flex items-center gap-1.5 text-sm font-medium"><Icon name="check" className="size-4 text-ok" />Patched same day</span> },
  ];
  return (
    <section className="mx-auto max-w-6xl px-6 py-8">
      <Reveal>
        <div className="pane hud overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
            <span className="kicker flex items-center gap-2"><span className="size-1.5 rounded-full bg-ok animate-pulse-soft" />live telemetry</span>
            <Link to="/status" className="kicker inline-flex items-center gap-1 hover:text-fg">full status <Icon name="external" className="size-3" /></Link>
          </div>
          <dl className="grid grid-cols-2 divide-line sm:grid-cols-3 lg:grid-cols-[repeat(4,minmax(0,1fr))_minmax(0,1.9fr)_minmax(0,1.2fr)] lg:divide-x">
            {cells.map((c, i) => (
              <div key={c.label} className={cn("flex flex-col justify-between gap-3 px-5 py-4", i > 0 && "border-t border-line sm:border-t-0 lg:border-t-0")}>
                <dt className="kicker text-[10px]">{c.label}</dt>
                <dd className="flex min-h-9 items-center">{c.value}</dd>
              </div>
            ))}
          </dl>
          <div className="border-t border-line px-5 py-4">
            <div className="mb-2.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-faint">
              <span>last 30 days</span>
              <span className="flex items-center gap-3">
                <span className="flex items-center gap-1"><span className="size-2 rounded-sm bg-ok" />ok</span>
                <span className="flex items-center gap-1"><span className="size-2 rounded-sm bg-warn" />issue</span>
                <span className="flex items-center gap-1"><span className="size-2 rounded-sm bg-line" />no data</span>
              </span>
            </div>
            {row && row.days.length > 0 ? <UptimeStrip days={row.days} /> : <div className="h-10 rounded-md border border-dashed border-line" />}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

// ── Pricing ──

function Pricing({ game }: { game: Game }) {
  const { me } = useAuth();
  const stripe = useStripeEnabled();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const best = bestIndex(game);

  const buy = async (i: number) => {
    if (!me) { navigate(`/register?next=${encodeURIComponent("/games/" + game.id)}`); return; }
    setBusy(i);
    setError(null);
    try {
      if (stripe) await startCheckout(game.id, i);
      else {
        const r = await api.post<{ ticket_id: number }>("/purchase-requests", { game_id: game.id, tier_index: i });
        toast("Purchase request sent", "success");
        navigate(`/support/${r.ticket_id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  if (!game.purchasable) {
    const discontinued = game.status === "discontinued";
    return (
      <section id="pricing" className="mx-auto max-w-6xl scroll-mt-24 px-6 py-20">
        <SectionHead kicker="// access" title={discontinued ? "No longer sold" : "Get notified"} />
        <Reveal>
          <div className="pane hud p-6 sm:p-8">
            <div className="max-w-xl"><NotForSale game={game} /></div>
            {discontinued && <Link to="/games" className="mt-5 inline-block"><Button icon="gamepad">Browse all games</Button></Link>}
          </div>
        </Reveal>
        {!discontinued && game.pricing_tiers.length > 0 && (
          <>
            <div className="kicker mb-3 mt-8">// launch pricing</div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Launch pricing, not yet on sale">
              {game.pricing_tiers.map((t, i) => (
                <div key={`${t.dur}-${i}`} className="pane flex flex-col p-5 opacity-60">
                  <span className="kicker">{t.dur}</span>
                  <span className="mt-2 font-display text-4xl font-semibold tabular-nums tracking-tight">{t.price}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-faint">Launch pricing may change before release.</p>
          </>
        )}
      </section>
    );
  }

  return (
    <section id="pricing" className="mx-auto max-w-6xl scroll-mt-24 px-6 py-20">
      <SectionHead kicker="// access" title="Pick your duration" />
      {game.pricing_tiers.length === 0 ? (
        <p className="rounded-[var(--radius-card)] border border-dashed border-line py-12 text-center text-sm text-muted">Pricing for {game.name} isn't published yet.</p>
      ) : (
        <>
          <ErrorText>{error}</ErrorText>
          <div className="mt-2 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {game.pricing_tiers.map((t, i) => (
              <Reveal key={`${t.dur}-${i}`} delay={i * 80}>
                <div className={cn("pane relative flex h-full flex-col p-5 transition-colors", i === best ? "glow-border" : "hover:border-line-strong")}
                  style={i === best ? { background: mix(9), borderColor: mix(50) } : undefined}>
                  {i === best && <span className="absolute -top-2 right-4 rounded bg-accent px-1.5 text-[10px] font-semibold uppercase leading-4 text-accent-fg">Best value</span>}
                  <span className="kicker">{t.dur}</span>
                  <span className="mt-2 font-display text-4xl font-semibold tabular-nums tracking-tight">{t.price}</span>
                  <Button size="md" variant={i === best ? "primary" : "secondary"} className="mt-6" loading={busy === i} disabled={busy !== null} onClick={() => buy(i)}>
                    {!me ? "Get started" : stripe ? "Buy now" : "Request"}
                  </Button>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-sm text-faint">
            Durations stack: buying more time adds to whatever you have left.{" "}
            {stripe ? "Payments are processed by Stripe. We never see your card details." : "Requests open a support ticket; an admin confirms payment and activates your license."}
          </p>
        </>
      )}
    </section>
  );
}

// ── Closing CTA ──

function ClosingCta({ game }: { game: Game }) {
  return (
    <section className="mx-auto max-w-6xl px-6 pb-12 pt-4">
      <Reveal>
        <div className="pane hud hud-accent glow-border relative overflow-hidden px-8 py-14 text-center sm:py-16" style={{ ["--c" as string]: "var(--game)" } as CSSProperties}>
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-[40rem] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px]" style={{ background: mix(22) }} />
          <LogoMark className="relative mx-auto mb-6 size-14" />
          <h2 className="relative text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl">Ready for <span style={{ color: "var(--game)" }}>{game.name}</span>?</h2>
          <p className="relative mx-auto mt-4 max-w-md text-muted">One loader, patched the same day the game updates.</p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-3">
            {game.purchasable && game.pricing_tiers.length > 0 && <Button variant="primary" size="lg" className="h-12 px-7" onClick={scrollToPricing}>See pricing</Button>}
            {!game.purchasable && <Button variant="primary" size="lg" icon="bell" className="h-12 px-7" onClick={scrollToPricing}>Notify me</Button>}
            <Link to="/games"><Button size="lg" className="h-12 px-7">All games</Button></Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function NotFound({ id }: { id: string }) {
  return (
    <section className="mx-auto flex min-h-[60vh] max-w-6xl flex-col items-start justify-center px-6 py-20">
      <div className="kicker mb-4 flex items-center gap-2 text-accent animate-rise"><span className="size-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--color-accent)]" />// error / 404</div>
      <h1 className="text-5xl font-bold uppercase leading-none tracking-tight animate-rise [animation-delay:80ms] sm:text-7xl">Signal <span className="text-gradient">lost</span><span className="text-accent">.</span></h1>
      <p className="mt-5 max-w-md text-lg text-muted animate-rise [animation-delay:160ms]">There's no game called <span className="font-mono text-fg">{id}</span> on ExoWare.</p>
      <Link to="/games" className="mt-8 animate-rise [animation-delay:240ms]"><Button variant="primary" size="lg" icon="gamepad">Browse all games</Button></Link>
    </section>
  );
}

export default function Product() {
  const { id = "" } = useParams();
  const game = useApi<Game>(`/games/${encodeURIComponent(id)}`);
  const status = useApi<PublicStatus>("/status");

  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  const [params, setParams] = useSearchParams();
  const cancelToast = useToast();
  useEffect(() => {
    if (params.get("checkout") === "cancelled") { cancelToast("Checkout cancelled. You weren't charged.", "info"); setParams({}, { replace: true }); }
  }, []);

  // `data` can still hold the previous game while the next one loads.
  const g = game.data && game.data.id === id ? game.data : undefined;
  useDocumentTitle(g?.name);
  if (!g) {
    if (game.error instanceof ApiError && game.error.status === 404) return <NotFound id={id} />;
    if (game.error) return <LoadError error={game.error} retry={game.reload} />;
    return <Loading />;
  }

  const theme = themeFor(g.id, g.category);
  return (
    <div style={{ ["--game" as string]: theme.a, ["--game2" as string]: theme.b } as CSSProperties}>
      <Hero game={g} />
      <Arsenal features={g.features} />
      <Telemetry game={g} status={status.data} />
      <Pricing game={g} />
      {g.status !== "discontinued" && <ClosingCta game={g} />}
    </div>
  );
}
